import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PageHeader, StatStrip } from "@/components/ui/page-header";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { PAYOUT_RATE_PER_CONTRIBUTION, PAYOUT_THRESHOLD, SUPPORT_EMAIL } from "./constants";
import type { EarningsCopy } from "./earnings-copy";
import { earningsSteps, type StepState } from "./earnings-steps";

export interface EarningsOverviewProps {
  copy: EarningsCopy;
  lang: string;
  validated: number;
  pending: number;
  /** 0 to 100, progress toward the threshold. */
  progress: number;
  remaining: number;
  thresholdReached: boolean;
  testSubmitted: boolean;
  testApproved: boolean;
  availableAmount: number;
  requestedAmount: number;
  hasPendingRequest: boolean;
  formatAmount: (amount: number) => string;
  formatNumber: (value: number) => string;
  onRequestPayout: () => void;
}

const fill = (text: string, values: Record<string, string | number>) =>
  Object.entries(values).reduce((out, [key, value]) => out.split(`{${key}}`).join(String(value)), text);

/**
 * "My earnings": where the contributor stands on the way to a payout, and the
 * one thing to do next. Replaces the old page that listed requirements, steps
 * and documents as separate blocks of text.
 */
export function EarningsOverview(props: EarningsOverviewProps) {
  const { copy, lang, formatAmount, formatNumber } = props;
  const steps = earningsSteps(props);
  const base = `/${lang}`;
  const rate = PAYOUT_RATE_PER_CONTRIBUTION;

  return (
    <div className="space-y-8">
      <PageHeader
        title={copy.title}
        description={copy.description}
        actions={
          steps.canRequestPayout ? (
            <Button size="sm" onClick={props.onRequestPayout}>
              <Icon name="wallet" />
              {copy.requestPayout}
            </Button>
          ) : null
        }
      />

      <ol className="grid gap-3 lg:grid-cols-3">
        <StepCard
          index={1}
          state={steps.contribute}
          copy={copy}
          icon="mic"
          title={fill(copy.step1Title, { threshold: PAYOUT_THRESHOLD })}
          action={
            steps.contribute === "current" ? (
              <Button asChild size="sm">
                <Link to={`${base}/speak-contribute`}>{copy.contribute}</Link>
              </Button>
            ) : null
          }
        >
          {steps.contribute === "done" ? (
            <p>{copy.step1Done}</p>
          ) : (
            <>
              <ProgressBar value={props.progress} label={`${formatNumber(props.validated)} / ${PAYOUT_THRESHOLD}`} />
              <p>{fill(copy.step1Remaining, { remaining: formatNumber(props.remaining) })}</p>
              {props.pending > 0 ? <p className="text-faint-foreground">{fill(copy.step1Pending, { count: formatNumber(props.pending) })}</p> : null}
            </>
          )}
        </StepCard>

        <StepCard
          index={2}
          state={steps.test}
          copy={copy}
          icon="fileText"
          title={copy.step2Title}
          action={
            steps.testPhase === "todo" ? (
              <Button asChild size="sm">
                <Link to={`${base}/get-paid?section=test`}>{copy.takeTest}</Link>
              </Button>
            ) : steps.testPhase === "review" ? (
              <Button asChild size="sm" variant="outline">
                <Link to={`${base}/get-paid?section=test`}>{copy.viewTest}</Link>
              </Button>
            ) : null
          }
        >
          <p>
            {steps.testPhase === "locked"
              ? copy.step2Locked
              : steps.testPhase === "review"
                ? copy.step2Review
                : steps.testPhase === "done"
                  ? copy.step2Done
                  : copy.step2Todo}
          </p>
        </StepCard>

        <StepCard
          index={3}
          state={steps.payout}
          copy={copy}
          icon="wallet"
          title={copy.step3Title}
          action={
            steps.canRequestPayout ? (
              <Button size="sm" onClick={props.onRequestPayout}>
                {copy.requestPayout}
              </Button>
            ) : props.hasPendingRequest ? (
              <Button asChild size="sm" variant="outline">
                <Link to={`${base}/get-paid?section=requests`}>{copy.viewRequests}</Link>
              </Button>
            ) : null
          }
        >
          {steps.payout === "locked" ? (
            <p>{fill(copy.step3Locked, { rate })}</p>
          ) : (
            <>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{formatAmount(props.availableAmount)}</p>
              <p>{fill(copy.step3Earning, { rate })}</p>
            </>
          )}
          {props.hasPendingRequest ? <p className="text-warning">{fill(copy.step3Pending, { amount: formatAmount(props.requestedAmount) })}</p> : null}
        </StepCard>
      </ol>

      <section aria-labelledby="earnings-figures" className="space-y-3">
        <h2 id="earnings-figures" className="text-sm font-medium text-muted-foreground">
          {copy.figuresTitle}
        </h2>
        <StatStrip
          items={[
            { label: copy.validated, value: formatNumber(props.validated) },
            { label: copy.pending, value: formatNumber(props.pending) },
            { label: copy.available, value: formatAmount(props.availableAmount), tone: props.availableAmount > 0 ? "success" : "default" },
            { label: copy.requested, value: formatAmount(props.requestedAmount), tone: props.requestedAmount > 0 ? "attention" : "default" },
          ]}
        />
      </section>

      <section aria-labelledby="earnings-before" className="rounded-lg border border-border/60 bg-surface p-5">
        <h2 id="earnings-before" className="text-sm font-semibold text-foreground">
          {copy.beforeTitle}
        </h2>
        <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
          {[copy.beforeId, copy.beforeTransfer, copy.beforeQuality].map((line) => (
            <li key={line} className="flex gap-2.5">
              <Icon name="check" className="mt-0.5 size-4 shrink-0 text-success" />
              {line}
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/60 pt-4 text-sm">
          <Button asChild size="sm" variant="outline">
            <Link to={`${base}/settings`}>{copy.completeProfile}</Link>
          </Button>
          <span className="text-muted-foreground">
            {copy.help}{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium text-brand underline-offset-4 hover:underline">
              {copy.contact}
            </a>
          </span>
        </div>
      </section>
    </div>
  );
}

function StepCard({
  index,
  state,
  copy,
  icon,
  title,
  action,
  children,
}: {
  index: number;
  state: StepState;
  copy: EarningsCopy;
  icon: IconName;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const badge = state === "done" ? copy.done : state === "current" ? copy.current : copy.locked;
  return (
    <li
      aria-current={state === "current" ? "step" : undefined}
      className={cn(
        "flex flex-col rounded-lg border p-5",
        state === "current" ? "border-brand/40 bg-brand/[0.04]" : "border-border/60 bg-surface",
        state === "locked" && "opacity-70",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-muted-foreground">{fill(copy.stepLabel, { n: index })}</span>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
            state === "done" && "bg-success/10 text-success",
            state === "current" && "bg-brand/10 text-brand",
            state === "locked" && "bg-surface-2 text-muted-foreground",
          )}
        >
          <Icon name={state === "done" ? "check" : state === "current" ? "clock" : "lock"} className="size-3" />
          {badge}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-2.5">
        <span className={cn("grid size-8 shrink-0 place-items-center rounded-md", state === "current" ? "bg-primary text-primary-foreground" : "bg-surface-2 text-muted-foreground")}>
          <Icon name={icon} className="size-4" />
        </span>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
      </div>
      <div className="mt-3 flex-1 space-y-2 text-sm text-muted-foreground">{children}</div>
      {action ? <div className="mt-4">{action}</div> : null}
    </li>
  );
}

function ProgressBar({ value, label }: { value: number; label: string }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="space-y-1.5">
      <div className="flex justify-end text-xs font-medium tabular-nums text-foreground">{label}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-border/70" role="progressbar" aria-valuenow={Math.round(clamped)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}
